import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Reward } from "../api/client";
import { REWARD_IMAGE_TYPES, RewardInput, rewardImageSrc, rewardsCatalogApi } from "../api/rewardsCatalog";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import { compressCertPhoto } from "../utils/certPhoto";
import "./RewardsCatalogPage.css";

type FormState = { title: string; description: string; count: string; imageURL: string | null };

const EMPTY_FORM: FormState = { title: "", description: "", count: "1", imageURL: null };

const formOf = (r: Reward): FormState => ({
  title: r.title,
  description: r.description ?? "",
  count: String(r.count),
  imageURL: r.image_url,
});

type ParsedCount = { ok: true; value: number } | { ok: false; hint: string };

// The server turns a count of zero or below into 1, so zero can't be saved.
function parseCount(text: string): ParsedCount {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return { ok: false, hint: "Укажите целое число от 1" };
  const value = Number(trimmed);
  if (value < 1) return { ok: false, hint: "На складе может быть от 1 шт.: ноль сервер сохраняет как 1" };
  return { ok: true, value };
}

export default function RewardsCatalogPage() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const list = await rewardsCatalogApi.list();
      setRewards(list ?? []);
    } catch (err: any) {
      console.error("Failed to load rewards", err);
      setError(err?.message || "Не удалось загрузить награды");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A full-size photo held as an object URL stays in memory until revoked,
  // so the previous one is released whenever the file changes or the page closes.
  useEffect(() => {
    if (!photo) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const editing = editingID === null ? null : rewards.find((r) => r.id === editingID) ?? null;
  const title = form.title.trim();
  const count = parseCount(form.count);
  const canSubmit = !busy && title !== "" && count.ok;

  const set = (changes: Partial<FormState>) => setForm((f) => ({ ...f, ...changes }));

  const clearPhoto = () => {
    setPhoto(null);
    setFileInputKey((k) => k + 1);
  };

  const pickPhoto = (file: File | undefined) => {
    if (!file) {
      setPhoto(null);
      return;
    }
    if (!REWARD_IMAGE_TYPES.includes(file.type)) {
      clearPhoto();
      setError("Картинка: подойдёт только JPEG, PNG, WebP или GIF");
      return;
    }
    setError(null);
    setPhoto(file);
  };

  const startEditing = (r: Reward) => {
    setError(null);
    setEditingID(r.id);
    setForm(formOf(r));
    clearPhoto();
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const stopEditing = () => {
    setEditingID(null);
    setForm(EMPTY_FORM);
    clearPhoto();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !count.ok) return;
    setBusy(true);
    setError(null);
    // Upload only on submit, not on file pick, so an abandoned form leaves
    // no orphan file on the server.
    let uploaded: string | null = null;
    try {
      if (photo) {
        try {
          // GIF is sent as is: re-encoding to JPEG would drop its animation.
          const file = photo.type === "image/gif" ? photo : await compressCertPhoto(photo);
          uploaded = await api.uploadImage(file);
        } catch (err: any) {
          throw new Error(`Не удалось загрузить картинку: ${err?.message || err}`);
        }
      }
      const input: RewardInput = {
        title,
        description: form.description.trim() || null,
        image_url: uploaded ?? form.imageURL,
        count: count.value,
      };
      if (editing) {
        await rewardsCatalogApi.update(editing.id, input);
      } else {
        await rewardsCatalogApi.create(input);
      }
      uploaded = null;
      stopEditing();
      await load();
    } catch (err: any) {
      console.error("Reward save failed", err);
      setError(err?.message || (editing ? "Не удалось сохранить награду" : "Не удалось добавить награду"));
      // The image went up just before the save failed, and nothing links to it.
      if (uploaded) {
        await api
          .deleteUploadedImage(uploaded)
          .catch((cleanupErr) => console.error("Failed to remove the reward image", cleanupErr));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: Reward) => {
    const confirmed = await confirm({
      title: `Удалить награду «${r.title}»?`,
      body: (
        <>
          <p>Награда пропадёт из каталога, и дети больше не смогут выбрать этот приз.</p>
          <p>
            Дети, которые уже выбрали его, но ещё не получили, останутся в очереди выдачи без приза.
            У выданных наград название приза тоже пропадёт.
          </p>
          <p>Награды тренеров с этим призом удалятся.</p>
          <p>Отменить удаление нельзя.</p>
        </>
      ),
      confirmLabel: "Удалить награду",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await rewardsCatalogApi.remove(r.id);
      if (editingID === r.id) stopEditing();
    } catch (err: any) {
      console.error("Reward delete failed", err);
      setError(err?.message || "Не удалось удалить награду");
    } finally {
      setBusy(false);
      await load();
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Каталог наград</h2>
      <p className="page-description">
        Призы, из которых победители месяца выбирают награду в приложении. Когда ребёнок выбирает
        приз, он списывается со склада. Приз, которого нет на складе, выбрать нельзя.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <form
        ref={formRef}
        className="mav-card cp-form reward-form"
        aria-label={editing ? "Изменение награды" : "Новая награда"}
        onSubmit={submit}
      >
        <h3 className="reward-form-title">{editing ? `Изменение: ${editing.title}` : "Новая награда"}</h3>
        <label className="cp-field">
          <span>Название</span>
          <input className="mav-input" value={form.title} maxLength={100} disabled={busy} onChange={(e) => set({ title: e.target.value })} />
        </label>
        <div className="cp-field">
          <label className="cp-field">
            <span>На складе, шт.</span>
            <input
              className={count.ok ? "mav-input" : "mav-input has-error"}
              type="number"
              min={1}
              step={1}
              value={form.count}
              disabled={busy}
              onChange={(e) => set({ count: e.target.value })}
            />
          </label>
          <span className={count.ok ? "mav-hint" : "mav-hint is-error"}>
            {count.ok ? "Когда ребёнок выбирает приз, списывается 1 шт." : count.hint}
          </span>
        </div>
        <label className="cp-field reward-description-field">
          <span>Описание</span>
          <input className="mav-input" value={form.description} maxLength={500} disabled={busy} onChange={(e) => set({ description: e.target.value })} />
        </label>
        <div className="cp-field reward-image-field">
          <label className="cp-field">
            <span>Картинка</span>
            <input
              key={fileInputKey}
              type="file"
              accept={REWARD_IMAGE_TYPES.join(",")}
              disabled={busy}
              onChange={(e) => pickPhoto(e.target.files?.[0])}
            />
          </label>
          {photo && photoUrl ? (
            <figure className="cp-photo-preview reward-image-preview">
              <img src={photoUrl} alt={`Картинка: ${photo.name}`} />
              <figcaption>
                <span>{photo.name}</span>
                <button type="button" className="mav-btn-secondary" disabled={busy} onClick={clearPhoto}>
                  Убрать картинку
                </button>
              </figcaption>
            </figure>
          ) : form.imageURL ? (
            <figure className="cp-photo-preview reward-image-preview">
              <img src={rewardImageSrc(form.imageURL)} alt="Текущая картинка" />
              <figcaption>
                <span>Текущая картинка</span>
                <button type="button" className="mav-btn-secondary" disabled={busy} onClick={() => set({ imageURL: null })}>
                  Убрать картинку
                </button>
              </figcaption>
            </figure>
          ) : (
            <span className="mav-hint">
              JPEG, PNG, WebP или GIF. Фото перед загрузкой сжимается до 2048 px, прозрачный фон
              станет белым; GIF загружается как есть.
            </span>
          )}
        </div>
        <div className="cp-actions mav-actions">
          <button type="submit" className="mav-btn-primary" disabled={!canSubmit}>
            {editing ? "Сохранить" : "Добавить награду"}
          </button>
          {editing && (
            <button type="button" className="mav-btn-secondary" disabled={busy} onClick={stopEditing}>
              Отмена
            </button>
          )}
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table reward-catalog">
          <thead>
            <tr>
              <th>Награда</th>
              <th>На складе</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rewards.length === 0 ? (
              <tr>
                <td colSpan={3} className="empty-cell">
                  {error ? "—" : "Наград пока нет"}
                </td>
              </tr>
            ) : (
              rewards.map((r) => (
                <tr key={r.id}>
                  <td className="cell-reward">
                    <div className="reward-summary">
                      {r.image_url ? (
                        <img className="reward-thumb" src={rewardImageSrc(r.image_url)} alt={r.title} />
                      ) : (
                        <span className="reward-thumb reward-thumb-empty" aria-hidden="true" />
                      )}
                      <div className="reward-text">
                        <span className="reward-title">{r.title}</span>
                        {r.description && <span className="reward-description">{r.description}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="cell-reward-count">
                    {r.count > 0 ? (
                      <span>{r.count} шт.</span>
                    ) : (
                      <span className="mav-badge mav-badge-muted">Нет на складе</span>
                    )}
                  </td>
                  <td className="cell-action">
                    <div className="row-actions">
                      <button className="mav-btn-secondary" disabled={busy} onClick={() => startEditing(r)}>
                        Изменить
                      </button>
                      <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(r)}>
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
